/* ============================================
   RADAR CHRONO - Chrono live dans le radar header
   ============================================
   Affiche le temps de jeu calcule a partir des
   messages MLive (10101) : st + tm[] + ot
   ============================================ */

import { state } from "../state.js";

const FIRST_HALF_END_SECONDS = 45 * 60;
const REGULATION_END_SECONDS = 90 * 60;
const EXTRA_TIME_END_SECONDS = 120 * 60;

const STATUS_PHASES = {
  1: "Pas commence",
  2: "1MT",
  3: "Mi-temps",
  4: "2MT",
  5: "Prolongation",
  6: "Pause prolongation",
  7: "TAB",
  8: "Termine",
};

const chronoState = {
  statusId: 0,
  periodStart: 0,
  periodOffset: 0,
  ot: 0,
  isRunning: false,
  phase: "",
  intervalId: null,
};

let els = null;
let sbEls = null;

let replayOffsetMs = 0;

/**
 * En mode replay, decaler Date.now() pour que le chrono voie
 * le timestamp du message rejoue comme etant "maintenant".
 * Appeler setReplayTimeOffset(Date.now() - messageTimestamp) avant chaque injection.
 * Remettre a 0 pour revenir au mode live.
 */
export function setReplayTimeOffset(offsetMs) {
  replayOffsetMs = offsetMs;
}

export function initRadarChrono() {
  els = {
    wrapper: document.getElementById("radar-chrono"),
    time: document.getElementById("radar-chrono-time"),
    phase: document.getElementById("radar-chrono-phase"),
    dot: document.getElementById("radar-chrono-dot"),
    added: document.getElementById("radar-chrono-added"),
  };
  sbEls = {
    wrapper: document.getElementById("stadium-scoreboard"),
    time: document.getElementById("sb-time"),
    phase: document.getElementById("sb-phase"),
    added: document.getElementById("sb-added"),
    homeLogo: document.getElementById("sb-home-logo"),
    awayLogo: document.getElementById("sb-away-logo"),
    homeScore: document.getElementById("sb-home-score"),
    awayScore: document.getElementById("sb-away-score"),
  };
}

function findTimestampInTm(tmArray) {
  if (!tmArray || !tmArray.length) return 0;
  for (let i = 0; i < tmArray.length; i++) {
    if (tmArray[i] > 1_000_000_000 && tmArray[i] < 2_000_000_000)
      return tmArray[i];
  }
  return 0;
}

function getPeriodOffset(st, tm) {
  if (tm && tm.length > 3 && tm[3] > 0) return tm[3];
  switch (st) {
    case 2:
      return 0;
    case 4:
      return FIRST_HALF_END_SECONDS;
    case 5:
      return REGULATION_END_SECONDS;
    default:
      return 0;
  }
}

export function updateRadarChrono(st, tm, ot) {
  if (!els || !els.wrapper) return;

  const periodStart = findTimestampInTm(tm);
  const prevRunning = chronoState.isRunning;
  const prevStatus = chronoState.statusId;
  const ticking = tm && tm.length > 0 ? tm[0] === 1 : false;

  chronoState.periodStart = periodStart;
  chronoState.periodOffset = getPeriodOffset(st, tm);
  chronoState.ot = ot || 0;

  let newRunning = false;
  let newPhase = STATUS_PHASES[st] || "";

  switch (st) {
    case 2:
    case 4:
    case 5:
      newRunning = ticking;
      break;
  }

  // st=5 sans ticking : distinguer attente ET vs pause mi-temps ET
  if (st === 5 && !newRunning) {
    newPhase = chronoState.periodOffset >= 6300 ? "Pause" : "Prolongation";
  }

  chronoState.statusId = st;
  chronoState.isRunning = newRunning;
  chronoState.phase = newPhase;

  if (state.batchReplay) return;

  els.wrapper.style.display = "flex";

  if (prevRunning !== newRunning || prevStatus !== st) {
    ensureTick();
  }

  if (!chronoState.intervalId) {
    render();
  }
}

export function forceRender() {
  if (els && els.wrapper) {
    els.wrapper.style.display = "flex";
    ensureTick();
  }
}

export function resetRadarChrono() {
  if (chronoState.intervalId) {
    clearInterval(chronoState.intervalId);
    chronoState.intervalId = null;
  }
  chronoState.statusId = 0;
  chronoState.periodStart = 0;
  chronoState.periodOffset = 0;
  chronoState.ot = 0;
  chronoState.isRunning = false;
  chronoState.phase = "";
  if (els && els.wrapper) {
    els.wrapper.style.display = "none";
  }
}

function getSeconds() {
  if (!chronoState.isRunning || chronoState.periodStart <= 0) {
    switch (chronoState.statusId) {
      case 2:
      case 4:
      case 5:
        return chronoState.periodOffset;
      case 3:
        return chronoState.periodOffset || FIRST_HALF_END_SECONDS;
      case 6:
        return chronoState.periodOffset || EXTRA_TIME_END_SECONDS / 2;
      case 7:
        return EXTRA_TIME_END_SECONDS;
      case 8:
        return REGULATION_END_SECONDS;
      default:
        return 0;
    }
  }
  const nowSec = Math.floor((Date.now() - replayOffsetMs) / 1000);
  const elapsed = Math.max(0, nowSec - chronoState.periodStart);
  return chronoState.periodOffset + elapsed;
}

function format(totalSeconds) {
  const min = Math.floor(totalSeconds / 60);
  const sec = totalSeconds % 60;
  return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

function syncLogo(imgEl, url) {
  if (!imgEl) return;
  if (url) {
    if (imgEl.getAttribute("src") !== url) imgEl.src = url;
    imgEl.style.display = "block";
  } else {
    imgEl.style.display = "none";
  }
}

function render() {
  if (!els) return;
  const secs = getSeconds();
  const st = chronoState.statusId;

  // Etats non-running : afficher le label au lieu du temps fige
  const isLabelState =
    !chronoState.isRunning &&
    (st === 1 || st === 3 || st === 5 || st === 6 || st === 7 || st === 8);

  const timeStr = isLabelState ? chronoState.phase : format(secs);

  els.time.textContent = timeStr;
  els.time.classList.toggle("running", chronoState.isRunning);
  els.time.classList.toggle("pause-label", isLabelState);
  els.dot.classList.toggle("running", chronoState.isRunning);
  els.phase.textContent = isLabelState ? "" : chronoState.phase;

  if (chronoState.ot > 0 && !isLabelState) {
    els.added.textContent = `+${chronoState.ot}`;
    els.added.style.display = "";
  } else {
    els.added.style.display = "none";
  }

  if (sbEls && sbEls.time) {
    const hideScoreboard =
      st !== 2 && st !== 4 && st !== 5 && st !== 6 && st !== 7 && st !== 8;

    if (sbEls.wrapper) {
      sbEls.wrapper.classList.toggle("sb-hidden", hideScoreboard);
    }

    sbEls.time.textContent = timeStr;
    sbEls.time.classList.toggle("running", chronoState.isRunning);
    sbEls.time.classList.toggle("pause-label", isLabelState);
    sbEls.added.textContent =
      chronoState.ot > 0 && !isLabelState ? `+${chronoState.ot}` : "";

    syncLogo(sbEls.homeLogo, state.teamLogos.home);
    syncLogo(sbEls.awayLogo, state.teamLogos.away);
  }
}

/**
 * Mettre a jour les logos des equipes dans le stadium scoreboard
 */
export function updateScoreboardLogos(homeLogo, awayLogo) {
  if (!sbEls) return;
  if (sbEls.homeLogo) {
    if (homeLogo) {
      sbEls.homeLogo.src = homeLogo;
      sbEls.homeLogo.style.display = "block";
    } else {
      sbEls.homeLogo.style.display = "none";
    }
  }
  if (sbEls.awayLogo) {
    if (awayLogo) {
      sbEls.awayLogo.src = awayLogo;
      sbEls.awayLogo.style.display = "block";
    } else {
      sbEls.awayLogo.style.display = "none";
    }
  }
}

/**
 * Mettre a jour le score dans le stadium scoreboard
 */
export function updateScoreboardScore(home, away) {
  if (!sbEls) return;
  if (sbEls.homeScore) sbEls.homeScore.textContent = String(home);
  if (sbEls.awayScore) sbEls.awayScore.textContent = String(away);
}

/**
 * Expose les donnees du chrono pour usage externe (commentary-context, etc.)
 */
export function getChronoData() {
  const secs = getSeconds();
  return {
    minute: Math.floor(secs / 60),
    seconds: secs,
    phase: chronoState.phase,
    statusId: chronoState.statusId,
    isRunning: chronoState.isRunning,
    addedTime: chronoState.ot,
  };
}

function ensureTick() {
  if (chronoState.intervalId) {
    clearInterval(chronoState.intervalId);
    chronoState.intervalId = null;
  }
  if (chronoState.isRunning) {
    chronoState.intervalId = setInterval(render, 1000);
  }
  render();
}
